import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type Account } from "../api/client";
import { queryKeys } from "../api/queryKeys";
import { useTheme } from "../contexts/ThemeContext";
import { AccountFormFields, type AccountFormValues } from "./AccountFormFields";

type Props = {
  account: Account;
  onSaved: () => void;
  onCancel: () => void;
};

/**
 * Modification d'un compte, en panneau de droite (pas de popup), même bloc de champs que
 * la création. Le mot de passe n'est pas touché ici : il se régénère depuis le détail du
 * compte, et il est lié au compte, pas au login.
 */
export function EditAccountPanel({ account, onSaved, onCancel }: Props) {
  const { t } = useTheme();
  const queryClient = useQueryClient();

  const { control, handleSubmit, getValues, setValue, watch } = useForm<AccountFormValues>({
    defaultValues: {
      login: account.login,
      nom: account.nom ?? "",
      // La valeur du champ est l'id du référentiel MAÎTRE, celui que renvoie la recherche.
      // Un compte non rattaché (comptes antérieurs) part avec un champ vide, à renseigner.
      idEntreprise: account.actilogCompanyId ?? "",
      nomEntreprise: account.nomEntreprise ?? "",
    },
  });

  const saveMut = useMutation({
    mutationFn: (values: AccountFormValues) =>
      api.accounts.update(account.idUserAPI, {
        login: values.login.trim(),
        nom: values.nom.trim(),
        idEntreprise: Number(values.idEntreprise),
        nomEntreprise: values.nomEntreprise.trim(),
      }),
    onSuccess: () => onSaved(),
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.accounts }),
  });

  const error = saveMut.error
    ? saveMut.error instanceof Error ? saveMut.error.message : "Erreur inconnue"
    : null;

  // Le login est l'identifiant d'authentification du client : on avertit dès qu'il change,
  // au moment où c'est utile plutôt que dans un texte permanent qu'on finit par ignorer.
  const loginChanged = watch("login").trim() !== account.login;

  const onSubmit = handleSubmit((values) => saveMut.mutate(values));

  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "18px 22px" }}>
      <div style={{ fontSize: 18, fontWeight: 700, color: t.text1 }}>Modifier le compte</div>
      <div style={{ fontSize: 12.5, color: t.text2, marginTop: 3, marginBottom: 18 }}>
        Compte n° {account.idUserAPI}. Le mot de passe n'est pas modifié ici.
      </div>

      <form onSubmit={onSubmit} style={{ display: "flex", flexDirection: "column", gap: 14, maxWidth: 460 }}>
        <AccountFormFields
          control={control}
          getValues={getValues}
          setValue={setValue}
          initialCompany={
            account.actilogCompanyId != null && account.nomEntreprise
              ? { value: account.actilogCompanyId, label: account.nomEntreprise }
              : undefined
          }
        />

        {loginChanged && (
          <div style={{ padding: "10px 12px", background: t.tagBg, color: t.tagColor, borderRadius: 8, fontSize: 12.5 }}>
            Le login passe de <b>{account.login}</b> à <b>{watch("login").trim() || "(vide)"}</b>. C'est
            l'identifiant avec lequel le client s'authentifie : son accès sera coupé jusqu'à ce qu'il
            mette sa configuration à jour.
          </div>
        )}

        {error && (
          <div style={{ padding: "10px 12px", background: t.lateBg, color: t.lateColor, borderRadius: 8, fontSize: 13 }}>
            {error}
          </div>
        )}

        <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
          <button type="submit" disabled={saveMut.isPending} style={{
            border: "none", background: t.btnBg, color: "#fff", borderRadius: 8,
            padding: "10px 16px", fontSize: 13.5, fontWeight: 600, fontFamily: "inherit",
            cursor: saveMut.isPending ? "not-allowed" : "pointer",
            opacity: saveMut.isPending ? 0.7 : 1,
          }}>
            {saveMut.isPending ? "Enregistrement..." : "Enregistrer"}
          </button>
          <button type="button" onClick={onCancel} style={{
            border: `1px solid ${t.rowBorder}`, background: t.cardBg, color: t.text2,
            borderRadius: 8, padding: "10px 14px", fontSize: 13.5, cursor: "pointer", fontFamily: "inherit",
          }}>Annuler</button>
        </div>
      </form>
    </div>
  );
}
